import { Alert, Button, Select, Space, Spin, TreeSelect } from "antd";
import type {
  DataLockCostConfigNode,
  DataLockCostLine,
} from "../../services/domains/data-lock/types";
import styles from "./DataLock.module.scss";

export type CostSheetScopeEditorValue = {
  restrictedGroups: string[];
  restrictedLines: DataLockCostLine[];
};

export type CostSheetScopeEditorProps = CostSheetScopeEditorValue & {
  groups: Array<{ code: string; name: string }>;
  configNodes: DataLockCostConfigNode[];
  loading?: boolean;
  error?: string | null;
  disabled?: boolean;
  onRetry?: () => void;
  onChange: (next: CostSheetScopeEditorValue) => void;
};

export type CostSheetGroupOption = {
  value: string;
  label: string;
};

export type CostSheetTreeNode = {
  key: string;
  value: string;
  title: string;
  selectable: boolean;
  checkable: boolean;
  disabled?: boolean;
  children?: CostSheetTreeNode[];
};

export type NormalizedCostConfigTree = {
  treeData: CostSheetTreeNode[];
  selectableLines: DataLockCostLine[];
};

const LINE_VALUE_PREFIX = "cost-line:";
const BRANCH_VALUE_PREFIX = "cost-branch:";
const RETAINED_VALUE_PREFIX = "cost-retained:";
const RETAINED_ROOT_VALUE = "cost-retained-root";

const nonEmptyString = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;

  const normalized = value.trim();
  return normalized || undefined;
};

export const costLineTreeValue = (configId: string) =>
  `${LINE_VALUE_PREFIX}${configId}`;

const costBranchTreeValue = (configId: string) =>
  `${BRANCH_VALUE_PREFIX}${configId}`;

const retainedCostLineTreeValue = (configId: string) =>
  `${RETAINED_VALUE_PREFIX}${configId}`;

const formatLineTitle = (line: DataLockCostLine) =>
  `${line.name} (${line.code})`;

export const normalizeCostGroups = (groups: unknown): CostSheetGroupOption[] => {
  if (!Array.isArray(groups)) return [];

  const seenCodes = new Set<string>();
  return groups.reduce<CostSheetGroupOption[]>((options, item) => {
    if (!item || typeof item !== "object") return options;

    const group = item as { code?: unknown; name?: unknown };
    const code = nonEmptyString(group.code);
    if (!code || code === "*" || seenCodes.has(code)) return options;

    seenCodes.add(code);
    options.push({
      value: code,
      label: nonEmptyString(group.name) ?? code,
    });
    return options;
  }, []);
};

export const normalizeCostConfigTree = (
  configNodes: unknown,
): NormalizedCostConfigTree => {
  if (!Array.isArray(configNodes)) {
    return { treeData: [], selectableLines: [] };
  }

  const seenConfigIds = new Set<string>();
  const visitedNodes = new WeakSet<object>();
  const selectableLines: DataLockCostLine[] = [];

  const normalizeNodes = (nodes: unknown[]): CostSheetTreeNode[] =>
    nodes.reduce<CostSheetTreeNode[]>((treeNodes, item) => {
      if (!item || typeof item !== "object" || visitedNodes.has(item)) {
        return treeNodes;
      }
      visitedNodes.add(item);

      const node = item as {
        _id?: unknown;
        code?: unknown;
        name?: unknown;
        active?: unknown;
        isLeaf?: unknown;
        children?: unknown;
      };
      const rawIsLeaf = node.isLeaf;
      const rawChildren = node.children;
      if (
        (rawIsLeaf !== undefined && typeof rawIsLeaf !== "boolean") ||
        (rawChildren !== undefined && !Array.isArray(rawChildren))
      ) {
        return treeNodes;
      }

      const originalChildren = Array.isArray(rawChildren)
        ? rawChildren
        : [];
      const isLeaf =
        typeof rawIsLeaf === "boolean"
          ? rawIsLeaf
          : originalChildren.length === 0;

      if (isLeaf) {
        const configId = nonEmptyString(node._id);
        const code = nonEmptyString(node.code);
        const name = nonEmptyString(node.name);
        if (
          node.active !== true ||
          !configId ||
          configId === "*" ||
          !code ||
          !name ||
          seenConfigIds.has(configId)
        ) {
          return treeNodes;
        }

        const line = { configId, code, name };
        seenConfigIds.add(configId);
        selectableLines.push(line);
        treeNodes.push({
          key: costLineTreeValue(configId),
          value: costLineTreeValue(configId),
          title: formatLineTitle(line),
          selectable: true,
          checkable: true,
        });
        return treeNodes;
      }

      const children = normalizeNodes(originalChildren);
      if (children.length === 0) return treeNodes;

      const configId = nonEmptyString(node._id);
      if (!configId || seenConfigIds.has(configId)) {
        treeNodes.push(...children);
        return treeNodes;
      }

      seenConfigIds.add(configId);
      const value = costBranchTreeValue(configId);
      treeNodes.push({
        key: value,
        value,
        title:
          nonEmptyString(node.name) ??
          nonEmptyString(node.code) ??
          configId,
        selectable: false,
        checkable: false,
        children,
      });
      return treeNodes;
    }, []);

  return {
    treeData: normalizeNodes(configNodes),
    selectableLines,
  };
};

const normalizeStringArray = (values: unknown): string[] => {
  if (!Array.isArray(values)) return [];

  const seen = new Set<string>();
  return values.reduce<string[]>((normalized, item) => {
    const value = nonEmptyString(item);
    if (!value || value === "*" || seen.has(value)) return normalized;

    seen.add(value);
    normalized.push(value);
    return normalized;
  }, []);
};

const normalizeRestrictedLines = (lines: unknown): DataLockCostLine[] => {
  if (!Array.isArray(lines)) return [];

  const seenConfigIds = new Set<string>();
  return lines.reduce<DataLockCostLine[]>((normalized, item) => {
    if (!item || typeof item !== "object") return normalized;

    const line = item as {
      configId?: unknown;
      code?: unknown;
      name?: unknown;
    };
    const configId = nonEmptyString(line.configId);
    if (!configId || configId === "*" || seenConfigIds.has(configId)) {
      return normalized;
    }

    seenConfigIds.add(configId);
    normalized.push({
      configId,
      code: nonEmptyString(line.code) ?? configId,
      name: nonEmptyString(line.name) ?? configId,
    });
    return normalized;
  }, []);
};

const cloneLine = (line: DataLockCostLine): DataLockCostLine => ({ ...line });

export const CostSheetScopeEditor = ({
  groups,
  configNodes,
  restrictedGroups,
  restrictedLines,
  loading = false,
  error = null,
  disabled = false,
  onRetry,
  onChange,
}: CostSheetScopeEditorProps) => {
  const groupOptions = normalizeCostGroups(groups);
  const selectedGroups = normalizeStringArray(restrictedGroups);
  const selectedLines = normalizeRestrictedLines(restrictedLines);
  const normalizedConfig = normalizeCostConfigTree(configNodes);
  const activeLinesById = new Map(
    normalizedConfig.selectableLines.map((line) => [line.configId, line]),
  );
  const activeLinesByValue = new Map(
    normalizedConfig.selectableLines.map((line) => [
      costLineTreeValue(line.configId),
      line,
    ]),
  );
  const retainedLines = selectedLines.filter(
    (line) => !activeLinesById.has(line.configId),
  );
  const retainedTree: CostSheetTreeNode[] = retainedLines.length
    ? [
        {
          key: RETAINED_ROOT_VALUE,
          value: RETAINED_ROOT_VALUE,
          title: "Dòng đã lưu không còn khả dụng",
          selectable: false,
          checkable: false,
          children: retainedLines.map((line) => ({
            key: retainedCostLineTreeValue(line.configId),
            value: retainedCostLineTreeValue(line.configId),
            title: `${formatLineTitle(line)} - Không còn khả dụng`,
            selectable: true,
            checkable: true,
          })),
        },
      ]
    : [];
  const treeData = [...normalizedConfig.treeData, ...retainedTree];
  const selectedTreeValues = selectedLines.map((line) =>
    activeLinesById.has(line.configId)
      ? costLineTreeValue(line.configId)
      : retainedCostLineTreeValue(line.configId),
  );
  const controlsDisabled = Boolean(disabled || loading);
  const errorMessage = nonEmptyString(error);

  const handleGroupChange = (nextGroups: string[]) => {
    if (controlsDisabled) return;

    onChange({
      restrictedGroups: normalizeStringArray(nextGroups),
      restrictedLines: selectedLines.map(cloneLine),
    });
  };

  const handleLineChange = (nextValues: string[]) => {
    if (controlsDisabled) return;

    const values = normalizeStringArray(nextValues);
    const selectedValueSet = new Set(values);
    const nextLines: DataLockCostLine[] = [];
    const seenConfigIds = new Set<string>();

    values.forEach((value) => {
      const line = activeLinesByValue.get(value);
      if (!line || seenConfigIds.has(line.configId)) return;

      seenConfigIds.add(line.configId);
      nextLines.push(cloneLine(line));
    });

    retainedLines.forEach((line) => {
      if (
        !selectedValueSet.has(retainedCostLineTreeValue(line.configId)) ||
        seenConfigIds.has(line.configId)
      ) {
        return;
      }

      seenConfigIds.add(line.configId);
      nextLines.push(cloneLine(line));
    });

    onChange({
      restrictedGroups: [...selectedGroups],
      restrictedLines: nextLines,
    });
  };

  return (
    <Space
      direction="vertical"
      size="middle"
      className={styles.CostScope}
    >
      {errorMessage ? (
        <Alert
          type="error"
          showIcon
          message={errorMessage}
          action={
            onRetry ? (
              <Button
                size="small"
                disabled={controlsDisabled}
                onClick={() => {
                  if (!controlsDisabled) onRetry();
                }}
              >
                Thử lại
              </Button>
            ) : undefined
          }
        />
      ) : null}

      <Spin
        spinning={Boolean(loading)}
        wrapperClassName={styles.CostSpin}
      >
        <Space
          direction="vertical"
          size="middle"
          className={styles.CostControls}
        >
          <div className={styles.CostControl}>
            <strong className={styles.FieldLabel}>Nhóm chi phí</strong>
            <Select<string[]>
              className={styles.Control}
              aria-label="Nhóm chi phí"
              mode="multiple"
              value={selectedGroups}
              options={groupOptions}
              placeholder="Tất cả nhóm"
              allowClear
              showSearch
              optionFilterProp="label"
              disabled={controlsDisabled}
              onChange={handleGroupChange}
            />
            <div>Để trống để áp dụng cho tất cả nhóm.</div>
          </div>

          <div className={styles.CostControl}>
            <strong className={styles.FieldLabel}>Dòng chi phí</strong>
            <div className={styles.CostTree}>
              <TreeSelect<string[]>
                className={styles.Control}
                aria-label="Dòng chi phí"
                value={selectedTreeValues}
                treeData={treeData}
                treeCheckable
                showCheckedStrategy={TreeSelect.SHOW_CHILD}
                showSearch
                treeNodeFilterProp="title"
                treeDefaultExpandAll
                allowClear
                placeholder="Tất cả dòng chi phí"
                notFoundContent="Không có dòng chi phí khả dụng"
                disabled={controlsDisabled}
                onChange={handleLineChange}
              />
            </div>
            <div>Để trống để áp dụng cho tất cả dòng chi phí.</div>
          </div>
        </Space>
      </Spin>
    </Space>
  );
};

export default CostSheetScopeEditor;
